const typewriter_delay_ms = 18;

export default {
  async send() {
    const outgoing = this.prepare_outgoing_message();
    if (!outgoing) return;
    this.is_sending = true;

    try {
      await this.send_outgoing_message(outgoing);
    } catch (error) {
      await this.handle_send_error(outgoing, error);
    } finally {
      this.is_sending = false;
    }
  },

  prepare_outgoing_message() {
    const text = this.user_message.trim();
    if (this.is_message_send_blocked(text)) return null;
    if (!this.active_conversation) this.start_new_conversation();

    const conversation_id = this.active_conversation_id;
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation) return null;

    conversation.messages.push(this.create_message("user", text));
    this.touch_conversation(conversation_id);
    this.user_message = "";
    const typing_message = this.create_typing_message();
    conversation.messages.push(typing_message);
    return ({ text, conversation_id, typing_message });
  },

  is_message_send_blocked(text) {
    return !text || this.is_sending;
  },

  async send_outgoing_message({ text, conversation_id, typing_message }) {
    const response_text = await this.query_assist(text, {
      conversation_id,
      message_id: typing_message.id,
    });
    this.complete_thinking_sequence(conversation_id, typing_message.id);
    await this.type_assistant_response(conversation_id, typing_message.id, response_text);
    await this.refresh_conversations_from_backend_safely(conversation_id);
  },

  async handle_send_error({ conversation_id, typing_message }, error) {
    this.fail_thinking_sequence(conversation_id, typing_message.id, error);
    await this.type_assistant_response(
      conversation_id,
      typing_message.id,
      error.message || "Siyanda Assist could not process that request."
    );
    await this.refresh_conversations_from_backend_safely(conversation_id);
  },

  clear_typewriter_timer() {
    if (!this.typewriter_timer) return;
    clearTimeout(this.typewriter_timer);
    this.typewriter_timer = null;
  },

  replace_message(conversation_id, message_id, message) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation) return;

    const index = conversation.messages.findIndex((item) => item.id === message_id);
    if (index === -1) conversation.messages.push(message);
    else conversation.messages.splice(index, 1, message);
    this.touch_conversation(conversation_id);
  },

  update_message_text(conversation_id, message_id, text) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation) return;

    const message = conversation.messages.find((item) => item.id === message_id);
    if (!message) return;
    message.text = text;
    this.touch_conversation(conversation_id);
  },

  update_thinking_steps(conversation_id, message_id, query_data) {
    const conversation = this.get_conversation_by_id(conversation_id);
    if (!conversation) return;

    const message = conversation.messages.find((item) => item.id === message_id);
    if (!message?.is_typing) return;
    message.thinking_steps = this.create_thinking_steps(query_data);
    this.touch_conversation(conversation_id);
  },

  wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  },

  complete_thinking_sequence(conversation_id, message_id) {
    const conversation = this.get_conversation_by_id(conversation_id);
    const message = conversation?.messages.find((item) => item.id === message_id);
    if (!message?.is_typing) return;

    message.thinking_steps = (message.thinking_steps || []).map((step) => ({
      ...step,
      status: step.status === "failed" ? "failed" : "complete",
    }));
    this.touch_conversation(conversation_id);
  },

  fail_thinking_sequence(conversation_id, message_id, error) {
    const conversation = this.get_conversation_by_id(conversation_id);
    const message = conversation?.messages.find((item) => item.id === message_id);
    if (!message?.is_typing) return;

    const steps = message.thinking_steps?.length
      ? message.thinking_steps
      : this.create_thinking_steps();
    message.thinking_steps = steps.map((step, index) => ({
      ...step,
      status: index === steps.length - 1 ? "failed" : step.status,
      detail: index === steps.length - 1
        ? error?.message || "The query job failed."
        : step.detail,
    }));
    this.touch_conversation(conversation_id);
  },

  async type_assistant_response(conversation_id, message_id, text) {
    const message = this.create_message("assistant", "");
    this.replace_message(conversation_id, message_id, message);
    const full_text = String(text || "");
    this.clear_typewriter_timer();

    for (let index = 1; index <= full_text.length; index += 1) {
      await this.wait(typewriter_delay_ms);
      this.update_message_text(conversation_id, message.id, full_text.slice(0, index));
    }
  },
};
